import * as React from 'react';

import { Box, Button, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'next-i18next';
import { object, string } from 'yup';

import { CONFIG } from '@/lib/config/config';

import { AssistantSignupComponent } from '../assistant/assistant-signup/assistant-signup.component';
import { ContentStyle, FormStyle, RootStyle, TextLink } from '../layout/layout.style';
import { useUserRequestMutation } from '../signup/queries/mutations/use-user-request.mutation';

const UserResendPasswordComponent = () => {
  const { t } = useTranslation('auth');
  const userRequestMutation = useUserRequestMutation();

  const emailVM = t('sign-up.validation.email.valid', { ns: 'auth' });
  const emailVMR = t('sign-up.validation.email.required', { ns: 'auth' });
  const formik = useFormik({
    initialValues: {
      email: ''
    },
    validationSchema: object({
      email: string().email(emailVM).max(255).required(emailVMR)
    }),
    onSubmit: (values) => {
      userRequestMutation.mutate({
        name: 'resend-password',
        payload: { email: values.email },
        withoutSession: true
      });
    }
  });

  return (
    <RootStyle>
      <ContentStyle>
        <AssistantSignupComponent step='forgotPassword' />
        <FormStyle>
          {userRequestMutation.isSuccess ? (
            <Box
              sx={{
                mt: 1,
                mb: 1,
                pt: 2,
                pb: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Typography
                data-cy='resend-password.success.headline'
                color='primary'
                variant='h3'
                pb={2}
                sx={{ textAlign: 'center' }}
              >
                {t('resend-password.success.headline', { ns: 'auth' })}
              </Typography>
              <Typography
                pb={5}
                color='text.primary'
                variant='body1'
                data-cy='resend-password.success.instructions'
              >
                {t('resend-password.success.instructions', { ns: 'auth' })}
              </Typography>
            </Box>
          ) : (
            <Box component='form' onSubmit={formik.handleSubmit} sx={{ mt: 1 }}>
              <Typography
                color='primary'
                variant='h3'
                display='flex'
                justifyContent='center'
                data-cy='resend-password.headline'
              >
                {t('resend-password.headline', { ns: 'auth' })}
              </Typography>
              <TextField
                error={Boolean(formik.touched.email && formik.errors.email)}
                fullWidth
                inputProps={{
                  'data-cy': 'resend-password.email'
                }}
                helperText={formik.touched.email && formik.errors.email}
                label={t('sign-up.email', { ns: 'auth' })}
                margin='normal'
                name='email'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                type='email'
                value={formik.values.email}
                variant='outlined'
                disabled={formik.isSubmitting}
              />
              <Button
                data-cy='resend-password.submit'
                type='submit'
                fullWidth
                disabled={formik.isSubmitting}
                variant='contained'
                sx={{ mt: 3, mb: 4 }}
              >
                {t('resend-password.submit', { ns: 'auth' })}
              </Button>
            </Box>
          )}
        </FormStyle>
        {!userRequestMutation.isSuccess && (
          <TextLink
            href={CONFIG.URL.LOGIN.path}
            title={t('resend-password.back', { ns: 'auth' })}
          />
        )}
      </ContentStyle>
    </RootStyle>
  );
};

export default UserResendPasswordComponent;
